import React, { useState, useEffect } from "react";
import Navbar from "./components/Navbar";
import Home from "./components/Home";
import Services from "./components/Services";
import About from "./components/About";
import Products from "./components/Products";
import BookingForm from "./components/BookingForm";
import RegistrationForm from "./components/RegistrationForm";
import AdminDashboard from "./components/AdminDashboard";
import CustomerDashboard from "./components/CustomerDashboard";
import AdminLogin from "./components/AdminLogin";
import StaffFirstLoginModal from "./components/StaffFirstLoginModal";
import Chatbot from "./components/Chatbot";
import Maintenance from "./components/Maintenance";
import FAQ from "./components/FAQ";
import Contact from "./components/Contact";
import ExternalQrNotice from "./components/ExternalQrNotice";
import PrivacyPolicy from "./components/PrivacyPolicy";
import TermsOfService from "./components/TermsOfService";
import axios from "axios";
import {
  PawIcon, FacebookIcon, InstagramIcon, TwitterIcon,
  MapPinIcon, PhoneIcon, ClockIcon, LockIcon, HeartIcon,
} from "./components/Icons";

// Configure axios for production
axios.defaults.baseURL = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5000`;

const MAINTENANCE_MODE = false; // Set to false to go live!

function App() {
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTabState] = useState("home");
  const [previousTab, setPreviousTab] = useState(null);
  const [adminLoginOpen, setAdminLoginOpen] = useState(false);
  const [externalQrScan, setExternalQrScan] = useState(null);
  const [serviceSection, setServiceSection] = useState(null);
  const [selectedBookingService, setSelectedBookingService] = useState(null);

  const setActiveTab = (tabOrFn) => {
    setActiveTabState((currentTab) => {
      const nextTab = typeof tabOrFn === "function" ? tabOrFn(currentTab) : tabOrFn;
      if (currentTab !== nextTab && nextTab === "booking") {
        setPreviousTab(currentTab);
      }
      return nextTab;
    });
  };

  // Detect external QR scans (e.g. from standard phone camera or external QR reader)
  useEffect(() => {
    const path = window.location.pathname;
    const parts = path.split("/").filter(Boolean);
    if (parts.length >= 3 && parts[0] === "pet") {
      setExternalQrScan({ type: "pet", userId: parts[1], petId: parts[2], rawPath: path });
    } else if (parts.length >= 2 && (parts[0] === "appointment" || parts[0] === "booking")) {
      setExternalQrScan({ type: "appointment", appointmentId: parts[1], rawPath: path });
    }
  }, []);

  useEffect(() => {
    const savedUser = localStorage.getItem("user");
    if (savedUser) {
      const parsedUser = JSON.parse(savedUser);
      setUser(parsedUser);
      setActiveTabState((parsedUser.role === "admin" || parsedUser.role === "staff") ? "admin" : "dashboard");
    }
  }, []);

  // Scroll to top for standalone pages (not for internal anchor sections like services/about)
  useEffect(() => {
    const inPageAnchorSections = ["services", "about"];
    if (!inPageAnchorSections.includes(activeTab)) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [activeTab]);

  // Manage browser history and back button when on booking page
  useEffect(() => {
    if (activeTab === "booking") {
      // Push history state so browser back button stays inside the app and triggers popstate
      window.history.pushState({ tab: "booking" }, "", "#booking");

      const handlePopState = () => {
        const fallback = user
          ? (user.role === "admin" || user.role === "staff" ? "admin" : "dashboard")
          : "home";
        setActiveTabState(previousTab || fallback);
      };

      window.addEventListener("popstate", handlePopState);
      return () => window.removeEventListener("popstate", handlePopState);
    } else {
      if (window.location.hash === "#booking") {
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
      }
    }
  }, [activeTab, previousTab, user]);

  const handleLogout = async () => {
    if (user && user.token) {
      try {
        await axios.post("/api/auth/logout", {}, {
          headers: { Authorization: `Bearer ${user.token}` }
        });
      } catch (err) {
        console.error("Logout error:", err);
      }
    }
    localStorage.removeItem("user");
    setUser(null);
    setActiveTab("home");
  };

  if (MAINTENANCE_MODE && (!user || (user.role !== "admin" && user.role !== "staff"))) {
    return (
      <>
        <Maintenance />
        <footer className="bg-[#fbf9f6] py-10 border-t border-[#e1ebf0] relative">
          <div className="container mx-auto px-6 relative flex items-center justify-center">
            {!user && (
              <button 
                onClick={() => setAdminLoginOpen(true)}
                className="absolute right-6 w-6 h-6 opacity-0 hover:opacity-100 transition-opacity text-xs cursor-default flex items-center justify-center"
              >
                🔒
              </button>
            )}
          </div>
        </footer>
        <AdminLogin 
          isOpen={adminLoginOpen} 
          onClose={() => setAdminLoginOpen(false)} 
          setUser={setUser}
          setActiveTab={setActiveTab}
        />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-100 to-orange-100">
      {activeTab !== "admin" && activeTab !== "dashboard" && !(user && activeTab === "booking") && (
        <Navbar
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          user={user}
          setUser={setUser}
          handleLogout={handleLogout}
          openAdminLogin={() => setAdminLoginOpen(true)}
        />
      )}

      <main className="transition-all duration-500">
        {/* ── Public single-page layout (no user logged in, not on a special tab) ── */}
        {!user && !['booking', 'register', 'products', 'faq', 'contact', 'privacy', 'terms'].includes(activeTab) && (
          <>
            <section id="home">
              <Home setActiveTab={setActiveTab} setServiceSection={setServiceSection} setSelectedBookingService={setSelectedBookingService} />
            </section>
            <section id="services">
              <Services setActiveTab={setActiveTab} serviceSection={serviceSection} setServiceSection={setServiceSection} />
            </section>
            <section id="about">
              <About />
            </section>
          </>
        )}

        {/* Products standalone page — accessible for all visitors */}
        {activeTab === "products" && (
          <Products setActiveTab={setActiveTab} />
        )}

        {/* FAQ standalone page — accessible for all visitors */}
        {activeTab === "faq" && (
          <FAQ />
        )}

        {/* Contact standalone page — accessible for all visitors */}
        {activeTab === "contact" && (
          <Contact />
        )}

        {/* Privacy Policy standalone page */}
        {activeTab === "privacy" && (
          <PrivacyPolicy setActiveTab={setActiveTab} />
        )}

        {/* Terms of Service standalone page */}
        {activeTab === "terms" && (
          <TermsOfService setActiveTab={setActiveTab} />
        )}        

        {/* Booking Form (accessible for guests and authenticated users) */}
        {activeTab === "booking" && (
          <BookingForm
            user={user}
            setUser={setUser}
            setActiveTab={setActiveTab}
            previousTab={previousTab}
            isAdminMode={user?.role === "admin" || user?.role === "staff"}
            preSelectedService={selectedBookingService}
            clearPreSelectedService={() => setSelectedBookingService(null)}
          />
        )}
        {!user && activeTab === "register" && (
          <RegistrationForm
            setUser={setUser}
            setActiveTab={setActiveTab}
          />
        )}
        {user && activeTab === "dashboard" && user.role !== "admin" && (
          <CustomerDashboard
            user={user}
            setUser={setUser}
            setActiveTab={setActiveTab}
            handleLogout={handleLogout}
          />
        )}
        {user && activeTab === "admin" && (user?.role === "admin" || user?.role === "staff") && (
          <AdminDashboard user={user} handleLogout={handleLogout} setActiveTab={setActiveTab} />
        )}
      </main>

      {activeTab !== "admin" && activeTab !== "dashboard" && !(user && activeTab === "booking") && <footer style={{ background: "linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #4c1d95 100%)", marginTop: "0", position: "relative", overflow: "hidden" }}>
        {/* Wave top */}
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, overflow: "hidden", lineHeight: 0 }}>
          <svg viewBox="0 0 1200 80" preserveAspectRatio="none" style={{ display: "block", width: "100%", height: "50px" }}>
            <path d="M0,40 C300,80 900,0 1200,40 L1200,0 L0,0 Z" fill="#faf5ff" />
          </svg>
        </div>

        {/* Background orbs */}
        <div style={{ position: "absolute", top: "2rem", left: "-5rem", width: "20rem", height: "20rem", borderRadius: "50%", background: "rgba(139,92,246,0.12)", filter: "blur(60px)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: "0", right: "-5rem", width: "18rem", height: "18rem", borderRadius: "50%", background: "rgba(236,72,153,0.08)", filter: "blur(60px)", pointerEvents: "none" }} />

        <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "5rem 1.5rem 2.5rem", position: "relative", zIndex: 1 }}>
          {/* Top grid */}
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: "3rem", marginBottom: "3rem", flexWrap: "wrap" }}>
            {/* Brand column */}
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1rem" }}>
                <div style={{ width: "2.8rem", height: "2.8rem", borderRadius: "0.9rem", background: "linear-gradient(135deg, #7C3AED, #EC4899)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 6px 20px rgba(124,58,237,0.4)" }}>
                  <PawIcon size={20} color="white" strokeWidth={1.6} />
                </div>
                <div>
                <div style={{ fontWeight: 900, fontSize: "1.2rem", color: "white", letterSpacing: "-0.01em" }}>Furpect Tails</div>
                <div style={{ fontSize: "0.7rem", color: "rgba(196,181,253,0.7)", fontWeight: 600 }}>PET GROOMING &amp; HOTEL</div>
              </div>
            </div>
            <p style={{ color: "rgba(196,181,253,0.7)", fontSize: "0.9rem", lineHeight: 1.75, maxWidth: "300px", margin: "0 0 1.5rem" }}>
                Giving your furry family members the premium care and love they deserve — every single visit.
              </p>
              {/* Social icons */}
              <div style={{ display: "flex", gap: "0.6rem" }}>
                {[
                  { Icon: FacebookIcon,  label: "Facebook",  url: "https://www.facebook.com/furpecttailsph/?rdid=LPdVAa4oseFdJsFu" },
                  { Icon: InstagramIcon, label: "Instagram", url: "https://www.instagram.com/furpecttailsph/?fbclid=IwY2xjawUD_Q5wZG9mA2V4dG4DYWVtAjExAHNydGMGYXBwX2lkATAAAR75QRVeqs-C_PT5EqmDU5_kBsHY_kOI6qhVgdSBFfSvTEyefSOlg_JqXrOf-w_aem_ta5HQghrEEmh6NL6hkvHCQ" },
                ].map(({ Icon, label, url }) => (
                  <a
                    key={label}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={label}
                    style={{ width: "2.4rem", height: "2.4rem", borderRadius: "0.7rem", background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.12)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", transition: "all 0.2s", textDecoration: "none" }}
                    onMouseEnter={e => { e.currentTarget.style.background = "rgba(124,58,237,0.4)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.08)"; e.currentTarget.style.transform = "translateY(0)"; }}
                  >
                    <Icon size={15} color="rgba(196,181,253,0.8)" />
                  </a>
                ))}
              </div>
            </div>
            {/* Contact info */}
            <div>
              <h4 style={{ fontWeight: 800, color: "white", fontSize: "0.9rem", marginBottom: "1.25rem", textTransform: "uppercase", letterSpacing: "0.08em" }}>Contact</h4>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                {[
                  { Icon: MapPinIcon, text: "96 Gen. Luna St., Tuktukan, Taguig City" },
                  { Icon: PhoneIcon,  text: "0954 498 8342"   },
                  { Icon: ClockIcon,  text: "Tue–Sun (Mon Closed) · 9 AM–7 PM" },
                ].map(({ Icon, text }) => (
                  <div key={text} style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <Icon size={15} color="rgba(196,181,253,0.6)" />
                    <span style={{ color: "rgba(196,181,253,0.75)", fontSize: "0.85rem", fontWeight: 500 }}>{text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Divider */}
          <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: "1.75rem", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "1rem" }}>
            <p style={{ color: "rgba(196,181,253,0.5)", fontSize: "0.8rem", margin: 0, display: "flex", alignItems: "center", gap: "0.35rem", flexWrap: "wrap" }}>
              &copy; {new Date().getFullYear()} Furpect Tails. All rights reserved. Made with
              <PawIcon size={13} color="rgba(196,181,253,0.6)" />
              &amp;
              <HeartIcon size={13} color="rgba(236,72,153,0.7)" />
            </p>
            <div style={{ display: "flex", gap: "1.5rem" }}>
              {[
                { label: "Privacy Policy", tab: "privacy" },
                { label: "Terms of Service", tab: "terms" },
              ].map(({ label, tab }) => (
                <button key={tab}
                  onClick={() => setActiveTab(tab)}
                  style={{
                    color: "rgba(196,181,253,0.4)", fontSize: "0.75rem",
                    cursor: "pointer", transition: "color 0.2s",
                    background: "none", border: "none", padding: 0, fontFamily: "inherit",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.color = "rgba(196,181,253,0.9)"; e.currentTarget.style.textDecoration = "underline"; }}
                  onMouseLeave={e => { e.currentTarget.style.color = "rgba(196,181,253,0.4)"; e.currentTarget.style.textDecoration = "none"; }}
                >{label}</button>
              ))}
            </div>
            {!user && (
              <button
                onClick={() => setAdminLoginOpen(true)}
                style={{ width: "1.5rem", height: "1.5rem", opacity: 0, cursor: "default", background: "none", border: "none", display: "flex", alignItems: "center", justifyContent: "center" }}
                onMouseEnter={e => { e.currentTarget.style.opacity = "1"; }}
                onMouseLeave={e => { e.currentTarget.style.opacity = "0"; }}
              >
                <LockIcon size={14} color="rgba(196,181,253,0.6)" />
              </button>
            )}
          </div>
        </div>
      </footer>}

      <AdminLogin 
        isOpen={adminLoginOpen} 
        onClose={() => setAdminLoginOpen(false)} 
        setUser={setUser}
        setActiveTab={setActiveTab}
      />

      {/* External QR Scan Notice (when scanned outside the admin scanner) */}
      {externalQrScan && (
        <ExternalQrNotice
          scanData={externalQrScan}
          onClose={() => setExternalQrScan(null)}
          onOpenAdminLogin={() => {
            setExternalQrScan(null);
            setAdminLoginOpen(true);
          }}
          user={user}
          onOpenInAdmin={() => {
            setExternalQrScan(null);
            setActiveTab("admin");
          }}
        />
      )}

      {/* Staff first-login forced setup */}
      {user?.isFirstLogin && (
        <StaffFirstLoginModal user={user} setUser={setUser} />
      )}

      {activeTab !== "admin" && activeTab !== "dashboard" && !(user && activeTab === "booking") && <Chatbot setActiveTab={setActiveTab} user={user} />}
    </div>
  );
}

export default App;
